'use client'

import { useActionState, useState, useEffect } from 'react'
import Link from 'next/link'
import { signInWithEmail, signInWithMagicLink } from '@/app/(auth)/actions'
import { reactivateAccount } from '@/app/(app)/settings/account/actions'
import { rethrowIfRedirect } from '@/components/auth/rethrow-redirect'
import { OAuthButtons } from '@/components/auth/oauth-buttons'
import { PasswordInput } from '@/components/auth/password-input'

interface LoginFormProps {
  checkEmail: boolean
  reactivate: boolean
  unlocked: boolean
  errorParam?: string
  /** Info-level banner (e.g. "you were signed out due to inactivity"). */
  infoParam?: string
  /** Email to prefill (e.g. redirected here from signup when it already exists). */
  prefillEmail?: string
}

export function LoginForm({ checkEmail, reactivate, unlocked, errorParam, infoParam, prefillEmail }: LoginFormProps) {
  const [oauthError, setOauthError] = useState<string | null>(null)
  const [email, setEmail] = useState(prefillEmail ?? '')
  const [emailError, setEmailError] = useState('')
  const [showUnlocked, setShowUnlocked] = useState(unlocked)
  const [mode, setMode] = useState<'password' | 'magic-link'>('password')

  const [error, formAction, isPending] = useActionState(
    async (_prev: string | null, formData: FormData) => {
      setOauthError(null)
      try {
        // Expected failures come back as { error } — thrown server-action
        // errors get masked by Next.js in production (see actions.ts).
        const res =
          mode === 'magic-link'
            ? await signInWithMagicLink(formData)
            : await signInWithEmail(formData)
        return res?.error ?? null
      } catch (e) {
        // A successful sign-in ends in redirect(), which rejects here
        // with NEXT_REDIRECT — rethrow so the router navigates.
        rethrowIfRedirect(e)
        return e instanceof Error ? e.message : 'Something went wrong'
      }
    },
    null,
  )

  useEffect(() => {
    if (unlocked) {
      const timer = setTimeout(() => setShowUnlocked(false), 5000)
      return () => clearTimeout(timer)
    }
  }, [unlocked])

  // Check email confirmation state
  if (checkEmail) {
    return (
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-bg-elevated)]">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M3 8l9 6 9-6" stroke="var(--color-brand-indigo)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <rect x="3" y="6" width="18" height="14" rx="2" stroke="var(--color-brand-indigo)" strokeWidth="2" fill="none" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]" style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}>
          Check your email
        </h1>
        <p className="text-sm text-[var(--color-text-secondary)]" style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}>
          We sent a confirmation link to your email. Click the link to verify your account.
        </p>
        <Link
          href="/login"
          className="text-sm font-medium text-[var(--color-brand-indigo)] hover:underline"
          style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}
        >
          Back to login &rarr;
        </Link>
      </div>
    )
  }

  // Account locked state (rate limited)
  if (error === 'Too many attempts') {
    return (
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-bg-elevated)]">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="5" y="11" width="14" height="10" rx="2" stroke="var(--color-error)" strokeWidth="2" />
            <path d="M8 11V7a4 4 0 018 0v4" stroke="var(--color-error)" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]" style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}>
          Account temporarily locked
        </h1>
        <p className="text-sm text-[var(--color-text-secondary)]" style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}>
          Too many failed sign-in attempts. We sent an unlock link to your email.
        </p>
        <Link
          href="/login"
          className="text-sm font-medium text-[var(--color-brand-indigo)] hover:underline"
          style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}
        >
          Back to login &rarr;
        </Link>
      </div>
    )
  }

  // Reactivation state (user is already authenticated, profile is soft-deleted)
  if (reactivate) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1.5 text-center">
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]" style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}>
            Welcome back
          </h1>
          <p className="text-sm text-[var(--color-text-secondary)]" style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}>
            Your account was scheduled for deletion. Sign in to reactivate it.
          </p>
        </div>
        <form action={reactivateAccount}>
          <button
            type="submit"
            className="flex h-12 w-full items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-brand-indigo)] text-sm font-semibold text-white transition-colors hover:brightness-110 disabled:cursor-default disabled:opacity-50"
            style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}
          >
            Reactivate my account
          </button>
        </form>
        <p className="text-center text-sm text-[var(--color-text-muted)]" style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}>
          <Link href="/" className="font-medium hover:underline">
            No, sign out
          </Link>
        </p>
      </div>
    )
  }

  function validateEmail(value: string) {
    const v = value.trim()
    if (!v) return 'This field is required'
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
    // Magic links can only go to an email address; password mode also
    // accepts a username (resolved server-side in signInWithEmail).
    if (mode === 'magic-link') {
      return isEmail ? '' : 'Enter a valid email address'
    }
    const isUsername =
      v.length >= 3 && v.length <= 30 && /^[a-z0-9_](?:[a-z0-9_-]*[a-z0-9_])?$/i.test(v)
    return isEmail || isUsername ? '' : 'Enter a valid email address or username'
  }

  const displayError =
    error === 'Invalid credentials'
      ? 'Invalid email, username, or password'
      : error || oauthError || errorParam || null

  return (
    <div className="flex flex-col gap-6">
      {/* Unlocked toast */}
      {showUnlocked && (
        <div
          className="fixed right-4 top-4 z-50 rounded-[var(--radius-md)] border border-[var(--color-success)]/30 bg-[var(--color-bg-surface)] px-4 py-3 shadow-lg"
          role="status"
          aria-live="polite"
          style={{ animation: 'toastSlideIn 0.3s ease-out both', fontFamily: 'var(--font-inter, Inter, sans-serif)' }}
        >
          <p className="text-sm text-[var(--color-success)]">Account unlocked. You can sign in now.</p>
        </div>
      )}

      <div className="flex flex-col gap-1.5 text-center">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]" style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}>
          Welcome back
        </h1>
        <p className="text-sm text-[var(--color-text-secondary)]" style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}>
          Sign in to your account
        </p>
      </div>

      <OAuthButtons onError={setOauthError} />

      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-[var(--color-bg-elevated)]" />
        <span className="text-xs text-[var(--color-text-muted)]" style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}>
          or continue with email
        </span>
        <div className="h-px flex-1 bg-[var(--color-bg-elevated)]" />
      </div>

      {infoParam && !displayError && (
        <div className="rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] p-3" role="status">
          <p className="text-sm text-[var(--color-text-secondary)]" style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}>
            {infoParam}
          </p>
        </div>
      )}

      {displayError && (
        <div className="rounded-[var(--radius-md)] border border-[var(--color-error)]/20 bg-[var(--color-error)]/10 p-3" role="alert">
          <p className="text-sm text-[var(--color-error)]" style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}>
            {displayError}
          </p>
        </div>
      )}

      <form action={formAction} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="login-email"
            className="text-sm font-medium text-[var(--color-text-secondary)]"
            style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}
          >
            {mode === 'password' ? 'Email or username' : 'Email'}
          </label>
          <input
            id="login-email"
            name="email"
            type={mode === 'password' ? 'text' : 'email'}
            required
            autoComplete={mode === 'password' ? 'username' : 'email'}
            value={email}
            aria-label={mode === 'password' ? 'Email or username' : 'Email address'}
            aria-invalid={!!emailError}
            aria-describedby={emailError ? 'login-email-error' : undefined}
            onBlur={(e) => setEmailError(validateEmail(e.target.value))}
            onChange={(e) => { setEmail(e.target.value); emailError && setEmailError('') }}
            className={`h-11 w-full rounded-[var(--radius-md)] border bg-[var(--color-bg-surface)] px-3 text-sm text-[var(--color-text-primary)] outline-none transition-colors placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-brand-indigo)] focus:ring-2 focus:ring-[var(--color-brand-indigo)]/25 ${emailError ? 'border-[var(--color-error)]' : 'border-[var(--color-text-muted)]'}`}
            style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}
          />
          {emailError && (
            <p id="login-email-error" className="text-xs text-[var(--color-error)]" role="alert">
              {emailError}
            </p>
          )}
        </div>

        {mode === 'password' && (
          <>
            <PasswordInput
              id="login-password"
              name="password"
              label="Password"
              required
              autoComplete="current-password"
            />

            <div className="flex justify-end">
              <Link
                href="/reset-password"
                className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
                style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}
              >
                Forgot password?
              </Link>
            </div>
          </>
        )}

        <button
          type="submit"
          disabled={isPending}
          className="flex h-12 w-full items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-brand-indigo)] text-sm font-semibold text-white transition-colors hover:brightness-110 disabled:cursor-default disabled:opacity-50"
          style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}
        >
          {isPending ? (
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          ) : mode === 'magic-link' ? (
            'Send magic link'
          ) : (
            'Sign in'
          )}
        </button>
      </form>

      <button
        type="button"
        onClick={() => { setMode(mode === 'password' ? 'magic-link' : 'password'); setEmailError('') }}
        className="text-center text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors"
        style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}
      >
        {mode === 'password' ? 'Sign in with magic link instead' : 'Sign in with password instead'}
      </button>

      <p className="text-center text-sm text-[var(--color-text-muted)]" style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}>
        Don&apos;t have an account?{' '}
        <Link href="/signup" className="font-medium text-[var(--color-brand-indigo)] hover:underline">
          Sign up &rarr;
        </Link>
      </p>
    </div>
  )
}
